import { useEffect } from 'react'
import {
  useUploadStore,
  UploadTask,
  getErrorMessage,
} from 'hooks/useUploadStore'
import { useCreateSubmission, useUpdateSubmission } from 'hooks/submissions'
import { useAuth } from 'util/auth'
import { uploadFile } from 'util/uploadFile'
import { getMediaDuration } from 'util/getMediaDuration'
import { getFileExtension } from 'util/getFileExtension'
import { validateFileExtension } from 'util/validateFileExtension'
import { apiRequest } from '../util/util'
import { Submission } from 'lib/types/generated-types'
import { useCredits } from './usage'
import { useRouter } from 'next/router'
import supabase from 'lib/supabase-browser'
import { uploadFileByUrl } from 'util/uploadFileByUrl'
import { useSetupStore } from 'hooks/useSetupStore'
import { toast } from 'react-hot-toast'
import { useProject } from './projects'
import { languageNameToCode } from 'util/languages'
import Sentry from 'util/sentry'

export function useUploadManager() {
  const {
    uploads,
    updateProgress,
    updateUpload,
    removeUpload,
    setShowInsufficientCreditsModal: setShownInsufficientCreditsModal,
  } = useUploadStore()
  const auth = useAuth()
  const router = useRouter()
  const { data: credits } = useCredits({
    projectId:
      Number(router.query.project_id) ||
      useSetupStore.getState().project?.id ||
      -1,
    userId: auth.user ? auth.user.id : '',
  })

  const { data: project } = useProject({
    projectId:
      Number(router.query.project_id) ||
      useSetupStore.getState().project?.id ||
      -1,
  })

  const projectLanguage = {
    language:
      Object.keys(languageNameToCode).find(
        (code) => languageNameToCode[code] === project?.language
      ) || 'English',
    language_code: project?.language || 'en_us',
  }

  const { mutateAsync: createSubmission } = useCreateSubmission()
  const { mutateAsync: updateSubmission } = useUpdateSubmission()

  // Add beforeunload event listener when there are active uploads
  useEffect(() => {
    const hasActiveUploads = uploads.some(
      (upload) =>
        upload.status === 'uploading' || upload.status === 'transcribing'
    )

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasActiveUploads) {
        e.preventDefault()
        e.returnValue =
          'You have uploads in progress. Are you sure you want to leave?'
        return e.returnValue
      }
    }

    if (hasActiveUploads) {
      window.addEventListener('beforeunload', handleBeforeUnload)
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [uploads])

  useEffect(() => {
    uploads.forEach((uploadTask) => {
      if (uploadTask.status === 'pending') {
        if (uploadTask.type === 'upload') {
          startUpload(uploadTask)
        } else if (uploadTask.type === 'youtube') {
          startYouTubeUpload(uploadTask)
        } else if (uploadTask.type === 'rss') {
          startRssUpload(uploadTask)
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploads])

  async function startUpload(uploadTask: UploadTask) {
    const { file, projectId, error, status } = uploadTask

    if (error || status === 'error') {
      updateUpload(uploadTask.id, {
        status: 'error',
        error: getErrorMessage(error) || 'Error',
      })
      Sentry.captureMessage('Error starting upload', {
        extra: {
          uploadTask,
        },
        level: 'error',
      })
      return
    }

    updateUpload(uploadTask.id, { status: 'uploading' })

    try {
      // Validate file extension
      if (!validateFileExtension(file?.name)) {
        Sentry.captureMessage('Invalid file type', {
          extra: {
            uploadTask,
          },
          level: 'error',
        })
        updateUpload(uploadTask.id, {
          status: 'error',
          error: 'Invalid file type',
        })
        return
      }

      // Get media duration
      const duration = await getMediaDuration(file)
      if (!duration) {
        Sentry.captureMessage('Unable to get duration', {
          extra: {
            uploadTask,
          },
          level: 'error',
        })
        updateUpload(uploadTask.id, {
          status: 'error',
          error: 'Unable to get duration',
        })
        return
      }

      if (!credits || typeof credits.projectOwnerCredits !== 'number') {
        updateUpload(uploadTask.id, {
          status: 'error',
          error:
            "You don't have any credits left for this month. Please consider purchasing more credits to continue.",
        })
        return
      }

      if (credits.projectOwnerCredits < Number(duration) / 3600) {
        updateUpload(uploadTask.id, {
          status: 'error',
          error: `Your project has ${(credits.projectOwnerCredits * 60).toFixed(
            1
          )} minutes remaining for this month. Please upload a shorter file or purchase more minutes.`,
        })
        setShownInsufficientCreditsModal(true)
        return
      }

      // Create submission
      const submissionData: Partial<Submission> = {
        owner: auth.user ? auth.user.id : '',
        filename: file?.name,
        status: 'uploading',
        duration_in_hours: duration / 3600,
        project_id: projectId,
        upload_method: uploadTask.type,
        is_speakers_filled_out: false,
        is_prompts_filled_out: false,
        ...projectLanguage,
      }

      const dbSubmission = await createSubmission(submissionData)

      updateUpload(uploadTask.id, { submissionId: dbSubmission.id })

      uploadTask.submissionId = dbSubmission.id

      const extension = getFileExtension(file?.name || '')
      const s3_file_key = `${dbSubmission.id}.${extension}`

      await updateSubmission({
        id: dbSubmission.id,
        data: { s3_file_key },
      })

      let hasCalledStartTranscription = false

      // Start checking the submission status
      checkSubmissionStatus(uploadTask)

      await uploadFile({
        file,
        s3_file_key,
        onProgress: (percentage) => {
          updateProgress(uploadTask.id, percentage)

          if (percentage === 100 && !hasCalledStartTranscription) {
            hasCalledStartTranscription = true
            setTimeout(() => {
              startTranscription(uploadTask)
              updateSubmission({
                id: dbSubmission.id,
                data: { status: 'transcribing' },
              })
              updateUpload(uploadTask.id, { status: 'transcribing' })
            }, 2000)
          }
        },
      })
    } catch (error) {
      Sentry.captureMessage('Error uploading file', {
        extra: {
          uploadTask,
        },
        level: 'error',
      })
      updateUpload(uploadTask.id, {
        status: 'error',
        error: getErrorMessage(error) || 'Error',
      })
    }
  }
  async function startYouTubeUpload(uploadTask: UploadTask) {
    const { videoUrl, projectId, id } = uploadTask
    updateUpload(id, { status: 'uploading' })

    if (!projectId) {
      throw new Error('Project ID is required')
    }

    if (!videoUrl) {
      throw new Error('Video URL is required')
    }

    try {
      // Create submission
      const submissionData: Partial<Submission> = {
        owner: auth.user ? auth.user.id : '',
        filename: '',
        status: 'uploading',
        duration_in_hours: 0,
        project_id: projectId,
        upload_method: uploadTask.type,
        is_speakers_filled_out: false,
        is_prompts_filled_out: false,
        ...projectLanguage,
      }

      const dbSubmission = await createSubmission(submissionData)
      uploadTask.submissionId = dbSubmission.id
      updateUpload(id, { submissionId: dbSubmission.id })

      // Get video metadata via Importly (async with webhook)
      const response = await apiRequest('download-yt', 'POST', {
        videoUrl,
        submissionId: dbSubmission.id,
      })

      if (!response.success) {
        const errorMsg =
          getErrorMessage(response.error) || 'Error triggering video upload'
        console.error('download-yt failed:', errorMsg, response)
        Sentry.captureMessage('Error triggering video upload', {
          extra: {
            uploadTask,
            response,
          },
          level: 'error',
        })
        updateUpload(id, {
          status: 'error',
          error: errorMsg,
        })
        return
      }

      updateUpload(id, { name: 'Processing...' })
      simulateProgress(id)
      checkSubmissionStatus(uploadTask)
    } catch (error) {
      console.error('YouTube upload error:', error)
      Sentry.captureMessage('Error processing video upload', {
        extra: {
          uploadTask,
        },
        level: 'error',
      })
      updateUpload(id, {
        status: 'error',
        error: getErrorMessage(error) || 'Error processing video upload',
      })
      if (uploadTask.submissionId) {
        updateSubmission({
          id: uploadTask.submissionId,
          data: { status: 'error' },
        })
      }
    }
  }

  async function checkSubmissionStatus(uploadTask: UploadTask) {
    const { id, submissionId } = uploadTask

    if (!submissionId) {
      updateUpload(id, {
        status: 'error',
        error: 'Error checking upload status',
      })
      return
    }

    const intervalId = setInterval(async () => {
      try {
        const { data, error } = await supabase
          .from('submissions')
          .select('status, s3_audio_key, filename, title')
          .eq('id', submissionId)
          .single()

        if (error) {
          Sentry.captureMessage('Error checking the upload status', {
            extra: {
              uploadTask,
            },
            level: 'error',
          })
          updateUpload(id, {
            status: 'error',
            error: 'Error checking the upload status.',
          })
          clearInterval(intervalId)
          return
        }

        // Fetch the latest uploadTask from the store
        const currentUploadTask = useUploadStore
          .getState()
          .uploads.find((u) => u.id === id)

        if (!currentUploadTask) {
          // If the uploadTask no longer exists, clear the interval
          clearInterval(intervalId)
          return
        }

        if (
          (data.filename || data.title) &&
          currentUploadTask.name === 'Processing...'
        ) {
          updateUpload(id, { name: (data.filename || data.title) as string })
        }

        if (data.status === 'Insufficient credits') {
          updateUpload(id, {
            status: 'error',
            error: 'Insufficient credits',
          })
          setShownInsufficientCreditsModal(true)
          clearInterval(intervalId)
          return
        }

        if (data.status === 'error' && currentUploadTask.status !== 'error') {
          Sentry.captureMessage('Error processing the file', {
            extra: {
              uploadTask,
            },
            level: 'error',
          })
          updateUpload(id, {
            status: 'error',
            error: 'Error processing the file',
          })
          clearInterval(intervalId)
        } else if (
          data.s3_audio_key &&
          currentUploadTask.status !== 'transcribing'
        ) {
          updateUpload(id, {
            status: 'transcribing',
          })
          setTimeout(() => {
            startTranscription(currentUploadTask)
          }, 2000)
        } else if (
          data.status === 'completed' &&
          currentUploadTask.status !== 'completed'
        ) {
          updateUpload(id, { status: 'completed' })
          removeUpload(id)
          toast.success(`${data.filename || data.title} is ready!`)
          clearInterval(intervalId)
        }
      } catch (err) {
        Sentry.captureMessage('Error checking the video upload status', {
          extra: {
            uploadTask,
          },
          level: 'error',
        })
        updateUpload(id, {
          status: 'error',
          error: 'Error checking the upload status.',
        })
        clearInterval(intervalId)
      }
    }, 1000)
  }

  async function startRssUpload(uploadTask: UploadTask) {
    const { episodeUrl, name, projectId, id } = uploadTask
    updateUpload(id, { status: 'uploading' })

    if (!projectId) {
      updateUpload(id, {
        status: 'error',
        error: 'Project ID is required',
      })
      return
    }

    try {
      // Fetch duration from the episode URL
      const resp = await fetch(
        `/api/get-duration-by-url?url=${encodeURIComponent(episodeUrl || '')}`
      )
      const { duration } = await resp.json()

      if (!duration) {
        Sentry.captureMessage('Unable to get duration of rss file', {
          extra: {
            uploadTask,
          },
          level: 'error',
        })
        updateUpload(id, {
          status: 'error',
          error: 'Unable to get duration of file.',
        })
        return
      }

      if (!credits || typeof credits.projectOwnerCredits !== 'number') {
        updateUpload(id, {
          status: 'error',
          error: 'Unable to fetch remaining credits.',
        })
        return
      }

      if (credits.projectOwnerCredits < Number(duration) / 3600) {
        updateUpload(id, {
          status: 'error',
          error: `Your project has ${(credits.projectOwnerCredits * 60).toFixed(
            1
          )} minutes remaining for this month. Please upload a shorter file or purchase more minutes.`,
        })
        setShownInsufficientCreditsModal(true)
        return
      }

      // Create submission
      const submissionData: Partial<Submission> = {
        owner: auth.user ? auth.user.id : '',
        filename: name || 'RSS Episode',
        status: 'uploading',
        duration_in_hours: duration / 3600,
        project_id: projectId,
        upload_method: uploadTask.type,
        is_speakers_filled_out: false,
        is_prompts_filled_out: false,
        ...projectLanguage,
      }

      const dbSubmission = await createSubmission(submissionData)
      uploadTask.submissionId = dbSubmission.id
      updateUpload(id, { submissionId: dbSubmission.id })

      const s3_file_key = `${dbSubmission.id}.mp3`

      await updateSubmission({
        id: dbSubmission.id,
        data: { s3_file_key },
      })

      let hasCalledStartTranscription = false

      // Start checking the submission status
      checkSubmissionStatus(uploadTask)

      await uploadFileByUrl({
        fileUrl: episodeUrl,
        s3_file_key,
        onProgress: (percentage) => {
          updateProgress(id, percentage)

          if (percentage === 100 && !hasCalledStartTranscription) {
            hasCalledStartTranscription = true
            setTimeout(() => {
              startTranscription(uploadTask)
              updateSubmission({
                id: dbSubmission.id,
                data: { status: 'transcribing' },
              })
              updateUpload(id, { status: 'transcribing' })
            }, 5000)
          }
        },
      })
    } catch (error) {
      Sentry.captureMessage('Error processing RSS upload', {
        extra: {
          uploadTask,
        },
        level: 'error',
      })
      updateUpload(id, {
        status: 'error',
        error: getErrorMessage(error) || 'Error processing RSS upload',
      })
    }
  }

  function simulateProgress(uploadId) {
    let progress = 0
    const interval = setInterval(() => {
      const uploadTask = useUploadStore
        .getState()
        .uploads.find((u) => u.id === uploadId)

      if (
        !uploadTask ||
        uploadTask.status === 'error' ||
        uploadTask.status === 'cancelled'
      ) {
        clearInterval(interval)
        return
      }

      // Adjust the increment based on the current progress
      if (progress < 90) {
        progress += Math.floor(Math.random() * 3) + 1 // Faster increments up to 95%
      } else {
        progress += Math.random() * 0.5 // Slow down significantly after 95%
      }

      // Ensure progress does not exceed 99
      if (progress >= 100) {
        progress = 99
        clearInterval(interval)
      }

      updateProgress(uploadId, progress)
    }, 500)
  }

  async function startTranscription(uploadTask: UploadTask) {
    const response = await apiRequest('transcribe', 'POST', {
      submission_id: uploadTask.submissionId,
      upload_method: uploadTask.type,
    })

    if (response.error) {
      Sentry.captureMessage('Error starting transcription', {
        extra: {
          uploadTask,
        },
        level: 'error',
      })
      updateUpload(uploadTask.id, {
        status: 'error',
        error: response.error,
      })
      return false
    }

    if (!uploadTask.submissionId) {
      return false
    }

    await updateSubmission({
      id: uploadTask.submissionId,
      data: { status: 'transcribing' },
    })
    return true
  }
}
